if (!customElements.get('product-form')) {
  // Shopify line item property uploads cap at 20 MB per file.
  const DEFAULT_UPLOAD_MAX_FILE_SIZE_MB = 20;

  const formatBytes = (bytes = 0) => {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';

    const units = ['B', 'KB', 'MB', 'GB'];
    const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / 1024 ** unitIndex;
    const decimals = unitIndex === 0 ? 0 : value >= 10 ? 0 : 1;

    return `${value.toFixed(decimals)} ${units[unitIndex]}`;
  };

  const getFileUploadFiles = (input) => Array.from(input?.files || []);

  const getFileUploadBlock = (input) => input?.closest('[data-file-upload-root]');

  const getFileUploadSettings = (input) => {
    const fileUploadBlock = getFileUploadBlock(input);
    const propertyName = fileUploadBlock?.dataset.fileUploadPropertyName?.trim() || 'Upload file';
    const maxFiles = Math.max(Number(fileUploadBlock?.dataset.fileUploadMaxFiles || 1) || 1, 1);
    const rawMaxFileSizeMb = Number(
      fileUploadBlock?.dataset.fileUploadMaxFileSizeMb || DEFAULT_UPLOAD_MAX_FILE_SIZE_MB
    );
    const maxFileSizeMb =
      Number.isFinite(rawMaxFileSizeMb) && rawMaxFileSizeMb > 0
        ? Math.min(rawMaxFileSizeMb, DEFAULT_UPLOAD_MAX_FILE_SIZE_MB)
        : DEFAULT_UPLOAD_MAX_FILE_SIZE_MB;

    return { fileUploadBlock, propertyName, maxFiles, maxFileSizeMb };
  };

  const setFileUploadError = (input, errorMessage = '') => {
    const fileUploadBlock = getFileUploadBlock(input);
    const errorElement = fileUploadBlock?.querySelector('[data-file-upload-error]');
    if (!fileUploadBlock || !errorElement) return;

    const hasError = Boolean(errorMessage);
    fileUploadBlock.classList.toggle('has-error', hasError);
    errorElement.hidden = !hasError;
    errorElement.textContent = errorMessage;
  };

  const setFileUploadSelectionMeta = (input, text = '') => {
    const fileUploadBlock = getFileUploadBlock(input);
    const metaElement = fileUploadBlock?.querySelector('[data-file-upload-meta]');
    if (!metaElement) return;

    metaElement.hidden = !text;
    metaElement.textContent = text;
  };

  const clearFileUploadPreviewUrls = (fileUploadBlock) => {
    if (!fileUploadBlock) return;

    const previewUrls = Array.isArray(fileUploadBlock._fileUploadPreviewUrls)
      ? fileUploadBlock._fileUploadPreviewUrls
      : [];

    if (typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
      previewUrls.forEach((previewUrl) => URL.revokeObjectURL(previewUrl));
    }

    fileUploadBlock._fileUploadPreviewUrls = [];
  };

  const getFileUploadExtensionLabel = (fileName = '') => {
    const extension = fileName.includes('.') ? fileName.split('.').pop() : 'FILE';
    return (extension || 'FILE').slice(0, 5).toUpperCase();
  };

  const getFileUploadPreviewStatusLabel = (status = 'ready', customLabel = '') => {
    if (customLabel) return customLabel;

    switch (status) {
      case 'uploading':
        return 'Uploading...';
      case 'uploaded':
        return 'Uploaded to cart';
      case 'error':
        return 'Upload failed';
      default:
        return 'Ready to upload';
    }
  };

  const renderFileUploadPreviewList = (input) => {
    const fileUploadBlock = getFileUploadBlock(input);
    const previewList = fileUploadBlock?.querySelector('[data-file-upload-preview-list]');
    if (!fileUploadBlock || !previewList) return;

    clearFileUploadPreviewUrls(fileUploadBlock);
    previewList.textContent = '';

    const files = getFileUploadFiles(input);
    if (!files.length) {
      previewList.hidden = true;
      return;
    }

    files.forEach((file) => {
      const previewItem = document.createElement('div');
      previewItem.className = 'product-form__file-upload-preview';

      const previewMedia = document.createElement('div');
      previewMedia.className = 'product-form__file-upload-preview-media';

      if (file.type.startsWith('image/') && typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
        const previewUrl = URL.createObjectURL(file);
        fileUploadBlock._fileUploadPreviewUrls.push(previewUrl);

        const previewImage = document.createElement('img');
        previewImage.className = 'product-form__file-upload-preview-image';
        previewImage.src = previewUrl;
        previewImage.alt = file.name;
        previewMedia.append(previewImage);
      } else {
        const previewBadge = document.createElement('span');
        previewBadge.className = 'product-form__file-upload-preview-badge';
        previewBadge.textContent = getFileUploadExtensionLabel(file.name);
        previewMedia.append(previewBadge);
      }

      const previewMeta = document.createElement('div');
      previewMeta.className = 'product-form__file-upload-preview-meta';

      const previewName = document.createElement('span');
      previewName.className = 'product-form__file-upload-preview-name';
      previewName.textContent = file.name;
      previewName.title = file.name;

      const previewSize = document.createElement('span');
      previewSize.className = 'product-form__file-upload-preview-size';
      previewSize.textContent = `${getFileUploadExtensionLabel(file.name)} · ${formatBytes(file.size)}`;

      const previewStatus = document.createElement('span');
      previewStatus.className = 'product-form__file-upload-preview-status';
      previewStatus.dataset.fileUploadPreviewStatus = 'ready';
      previewStatus.textContent = getFileUploadPreviewStatusLabel();

      previewMeta.append(previewName, previewSize);
      previewItem.append(previewMedia, previewMeta, previewStatus);
      previewList.append(previewItem);
    });

    previewList.hidden = false;
  };

  const setFileUploadPreviewStatus = (input, status = 'ready', customLabel = '') => {
    const fileUploadBlock = getFileUploadBlock(input);
    const previewList = fileUploadBlock?.querySelector('[data-file-upload-preview-list]');
    if (!previewList || previewList.hidden) return;

    previewList.querySelectorAll('[data-file-upload-preview-status]').forEach((statusElement) => {
      statusElement.dataset.fileUploadPreviewStatus = status;
      statusElement.textContent = getFileUploadPreviewStatusLabel(status, customLabel);
    });
  };

  const setFileUploadProgress = (input, progress = 0, label = '') => {
    const fileUploadBlock = getFileUploadBlock(input);
    const progressElement = fileUploadBlock?.querySelector('[data-file-upload-progress]');
    const progressFill = fileUploadBlock?.querySelector('[data-file-upload-progress-fill]');
    const progressText = fileUploadBlock?.querySelector('[data-file-upload-progress-text]');
    if (!fileUploadBlock || !progressElement || !progressFill || !progressText) return;

    const hasLabel = Boolean(label);
    progressElement.hidden = !hasLabel;
    progressFill.style.width = hasLabel ? `${Math.max(0, Math.min(progress, 100))}%` : '0%';
    progressText.textContent = label;
    fileUploadBlock.classList.toggle('is-uploading', hasLabel);
  };

  const validateFileUploadInput = (input) => {
    const files = getFileUploadFiles(input);
    if (!files.length) return '';

    const { maxFiles, maxFileSizeMb } = getFileUploadSettings(input);
    if (files.length > maxFiles) {
      return `You can upload up to ${maxFiles} ${maxFiles === 1 ? 'file' : 'files'} for this product.`;
    }

    const maxFileSizeBytes = maxFileSizeMb * 1024 * 1024;
    const oversizedFile = files.find((file) => file.size > maxFileSizeBytes);
    if (oversizedFile) {
      return `${oversizedFile.name} is ${formatBytes(
        oversizedFile.size
      )}. Shopify only accepts files up to ${maxFileSizeMb} MB in this upload field.`;
    }

    return '';
  };

  const updateFileUploadState = (input) => {
    if (!input) return;

    const fileUploadBlock = getFileUploadBlock(input);
    const fileUploadName = fileUploadBlock?.querySelector('[data-file-upload-name]');
    if (!fileUploadBlock || !fileUploadName) return;

    const defaultFileUploadName =
      fileUploadName.dataset.defaultFileUploadName || fileUploadName.textContent.trim();
    const files = getFileUploadFiles(input);

    if (!files.length) {
      fileUploadName.dataset.defaultFileUploadName = defaultFileUploadName;
      fileUploadName.textContent = defaultFileUploadName;
      fileUploadName.title = defaultFileUploadName;
      fileUploadBlock.classList.remove('is-filled');
      setFileUploadSelectionMeta(input, '');
      setFileUploadError(input, '');
      setFileUploadProgress(input);
      renderFileUploadPreviewList(input);
      return;
    }

    const totalBytes = files.reduce((size, file) => size + file.size, 0);
    const summaryLabel = files.length === 1 ? files[0].name : `${files.length} files selected`;
    const summaryTitle = files.map((file) => file.name).join(', ');
    const selectionMeta =
      files.length === 1
        ? `${formatBytes(files[0].size)} ready to upload`
        : `${files.length} files, ${formatBytes(totalBytes)} total, ready to upload`;
      const validationError = validateFileUploadInput(input);

    fileUploadName.dataset.defaultFileUploadName = defaultFileUploadName;
    fileUploadName.textContent = summaryLabel;
    fileUploadName.title = summaryTitle;
    fileUploadBlock.classList.add('is-filled');
    setFileUploadSelectionMeta(input, selectionMeta);
      setFileUploadError(input, validationError);
    setFileUploadProgress(input);
      renderFileUploadPreviewList(input);
      setFileUploadPreviewStatus(input, validationError ? 'error' : 'ready', validationError ? 'Check file limits' : '');
  };

  const parseResponsePayload = (responseText, status = 0) => {
    if (!responseText) {
      return status ? { status } : {};
    }

    try {
      return JSON.parse(responseText);
    } catch (error) {
      return {
        status: status || 500,
        description: 'We could not read Shopify\'s upload response. Please try again.',
      };
    }
  };

  const sendAddToCartRequest = (formData, headers, onProgress = null) => {
    if (!onProgress) {
      return fetch(`${routes.cart_add_url}`, { method: 'POST', headers, body: formData }).then(async (response) => {
        const responseText = await response.text();
        const payload = parseResponsePayload(responseText, response.ok ? 0 : response.status);

        if (!response.ok && !payload.status) {
          payload.status = response.status;
        }

        return payload;
      });
    }

    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open('POST', routes.cart_add_url, true);
      request.timeout = 240000;

      Object.entries(headers).forEach(([name, value]) => request.setRequestHeader(name, value));

      request.upload.addEventListener('progress', (event) => {
        if (!event.lengthComputable) return;
        onProgress(Math.min(Math.round((event.loaded / event.total) * 100), 99));
      });

      request.addEventListener('load', () => {
        const payload = parseResponsePayload(request.responseText, request.status);

        if (request.status >= 200 && request.status < 300) {
          resolve(payload);
          return;
        }

        reject(payload);
      });

      request.addEventListener('error', () => {
        reject({
          status: request.status || 500,
          description: 'The upload failed before Shopify could accept the files. Try smaller files or fewer files.',
        });
      });

      request.addEventListener('timeout', () => {
        reject({
          status: request.status || 408,
          description: 'The upload timed out. Try again with smaller files or fewer files.',
        });
      });

      request.send(formData);
    });
  };

  document.querySelectorAll('[data-file-upload-input]').forEach((input) => updateFileUploadState(input));

  document.addEventListener('change', (event) => {
    if (event.target instanceof HTMLInputElement && event.target.matches('[data-file-upload-input]')) {
      updateFileUploadState(event.target);
    }
  });

  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.error = false;
      }

      getFileUploadInputs() {
        return Array.from(document.querySelectorAll(`[data-file-upload-input][form="${this.form.id}"]`)).filter(
          (input) => input instanceof HTMLInputElement
        );
      }

      validateFileUploads(uploadInputs) {
        for (const input of uploadInputs) {
          const errorMessage = validateFileUploadInput(input);
          setFileUploadError(input, errorMessage);
          setFileUploadPreviewStatus(input, errorMessage ? 'error' : 'ready', errorMessage ? 'Check file limits' : '');

          if (errorMessage) {
            return { input, errorMessage };
          }
        }

        return null;
      }

      appendFileUploadProperties(formData, uploadInputs) {
        uploadInputs.forEach((input) => {
          if (!input.name) return;

          const files = getFileUploadFiles(input);
          const { propertyName } = getFileUploadSettings(input);
          formData.delete(input.name);

          if (!files.length) return;

          files.forEach((file, index) => {
            const propertyLabel = files.length === 1 ? propertyName : `${propertyName} ${index + 1}`;
            formData.append(`properties[${propertyLabel}]`, file, file.name);
          });
        });
      }

      toggleFileUploadInputs(uploadInputs, disabled) {
        uploadInputs.forEach((input) => {
          input.disabled = disabled;
        });
      }

      setFileUploadProgressState(uploadInputs, progress, label) {
        uploadInputs.forEach((input) => setFileUploadProgress(input, progress, label));
      }

      getResponseErrorMessage(response) {
        if (!response) return 'We could not add this item to the cart. Please try again.';

        if (typeof response.errors === 'string' && response.errors.trim() !== '') {
          return response.errors;
        }

        if (response.errors && typeof response.errors === 'object') {
          const nestedErrorMessage = Object.values(response.errors)
            .flat()
            .filter(Boolean)
            .join(' ')
            .trim();
          if (nestedErrorMessage) return nestedErrorMessage;
        }

        if (typeof response.description === 'string' && response.description.trim() !== '') {
          return response.description;
        }

        if (typeof response.message === 'string' && response.message.trim() !== '') {
          return response.message;
        }

        return 'We could not add this item to the cart. Please try again.';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const uploadInputs = this.getFileUploadInputs();
        const hasSelectedUploads = uploadInputs.some((input) => getFileUploadFiles(input).length > 0);
        const validationError = this.validateFileUploads(uploadInputs);
        if (validationError) {
          this.handleErrorMessage(validationError.errorMessage);
          validationError.input.focus();
          return;
        }

        this.handleErrorMessage();
        uploadInputs.forEach((input) => setFileUploadError(input, ''));
        this.error = false;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');
        this.toggleFileUploadInputs(uploadInputs, true);
        this.setFileUploadProgressState(uploadInputs, 0, hasSelectedUploads ? 'Uploading 0%' : '');
        if (hasSelectedUploads) {
          uploadInputs.forEach((input) => setFileUploadPreviewStatus(input, 'uploading', 'Uploading 0%'));
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        this.appendFileUploadProperties(formData, uploadInputs);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        sendAddToCartRequest(formData, config.headers, hasSelectedUploads ? (progress) => {
          this.setFileUploadProgressState(uploadInputs, progress, `Uploading ${progress}%`);
          uploadInputs.forEach((input) => setFileUploadPreviewStatus(input, 'uploading', `Uploading ${progress}%`));
        } : null)
          .then((response) => {
            if (response.status) {
              const errorMessage = this.getResponseErrorMessage(response);
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(errorMessage);
              if (hasSelectedUploads) {
                uploadInputs.forEach((input) => {
                  setFileUploadError(input, errorMessage);
                  setFileUploadProgress(input);
                  setFileUploadPreviewStatus(input, 'error');
                });
              }
              this.error = true;
              return;
            } else if (!this.cart) {
              if (hasSelectedUploads) {
                this.setFileUploadProgressState(uploadInputs, 100, 'Upload complete');
                uploadInputs.forEach((input) => setFileUploadPreviewStatus(input, 'uploaded'));
              }
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            if (hasSelectedUploads) {
              this.setFileUploadProgressState(uploadInputs, 100, 'Upload complete');
                uploadInputs.forEach((input) => setFileUploadPreviewStatus(input, 'uploaded'));
            }
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((error) => {
            const errorMessage = this.getResponseErrorMessage(error);
            this.error = true;
            this.handleErrorMessage(errorMessage);

            if (hasSelectedUploads) {
              uploadInputs.forEach((input) => {
                setFileUploadError(input, errorMessage);
                setFileUploadProgress(input);
                setFileUploadPreviewStatus(input, 'error');
              });
            }

            console.error(error);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            this.toggleFileUploadInputs(uploadInputs, false);

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
